import * as React from "react";
import { useMinimumWageQuery } from "../useMinimumWageQuery";
import { MinimalWagePageProps } from "../types";
import { useMinimumWageMutation } from "../useMinimumWageMutation";
import { useLocalize } from "@/modules/shared/context/localize/LocalizeContext";
import { MinimumWageCountryLayout } from "../components/MinimumWageCountryLayout";

import { PolishMinimumWageConfigurationModel } from "@/app/models/configuration/minimumWage/polishMinimumWageConfigurationModel";
import { PolishMinMonthlyRateExpandableItemSection } from "../containers/PolishMinMonthlyRateExpandableItemSection";
import { PolishMinHourRateExpandableItemSection } from "../containers/PolishMinHourRateExpandableItemSection";
import { PolishOptionsSection } from "../containers/PolishOptionsSection";

import { PreLoader } from "../components/PreLoader"
import { buildMinimumWageDiffRenderers } from '../minimumWageDiffUtils';

const country = 'poland'

export const PolandMinimumWageSettingsPage = ({ match }: MinimalWagePageProps) => {
    const { companyId } = match.params
    const q = useMinimumWageQuery(companyId)
    const up = useMinimumWageMutation(companyId)
    const { t } = useLocalize()

    const [state, setState] = React.useState<PolishMinimumWageConfigurationModel | null>(null)

    React.useEffect(() => {
        setState(null)
    }, [companyId])

    React.useEffect(() => {
        if (q.data) {
            const newState = structuredClone(q.data.polishConfiguration)
            setState(newState)
        }
    }, [q.data])

    const diffRenderers = React.useMemo(() => buildMinimumWageDiffRenderers('', t), [t])
    const diffLabels = React.useMemo<Record<string, string>>(() => ({
        configuration: t('configuration.minimumWages.options'),
        expandableItems: t('configuration.minimumWages.minimalMonthRateName'),
        hourRateExpandableItems: t('configuration.minimumWages.minimalHourRateName'),
    }), [t])

    if (!state || !q.data) {
        return <PreLoader />
    }

    const isDirty = JSON.stringify(state) !== JSON.stringify(q.data.polishConfiguration)

    return (
        <MinimumWageCountryLayout
            selectedCountryValue={country}
            isDirty={isDirty}
            handleSave={() => up.mutateAsync({ ...q.data, polishConfiguration: state })}
            handleCancel={() => setState(q.data.polishConfiguration)}
            dataTestId="poland-minimum-wage-settings-page"
            isLoading={(q.isFetching || up.isPending)}
            diffBefore={q.data.polishConfiguration as Record<string, unknown>}
            diffAfter={state as Record<string, unknown>}
            diffLabels={diffLabels}
            diffRenderers={diffRenderers}
        >
            <PolishMinMonthlyRateExpandableItemSection
                state={state}
                setState={setState}
            />

            <PolishMinHourRateExpandableItemSection
                state={state}
                setState={setState}
            />

            <PolishOptionsSection
                state={state}
                setState={setState} 
            />

            <div style={{ minHeight: '90px' }} />
        </MinimumWageCountryLayout>
    )
}
